import { useEffect, useState } from "react";
import PageHeader from "../components/layout/PageHeader";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "../components/ui/select";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { autoLotteryAbbr } from "../lib/ticketFormat";

const DAYS = ["L", "M", "X", "J", "V", "S", "D"];
const EMPTY = {
  operator_id: "", code: "", name: "",
  draw_time: "20:55", closing_time: "20:45",
  active_days: [0, 1, 2, 3, 4, 5, 6],
  number_digits: 2, short_code: "", active: true,
};

export default function Games() {
  const [items, setItems] = useState([]);
  const [operators, setOperators] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    const [g, o] = await Promise.all([api.get("/games"), api.get("/operators")]);
    setItems(g.data); setOperators(o.data);
  };
  useEffect(() => { load(); }, []);

  const toggleDay = (d) => setForm((f) => ({
    ...f,
    active_days: f.active_days.includes(d) ? f.active_days.filter(x => x !== d) : [...f.active_days, d].sort(),
  }));

  const save = async () => {
    try {
      if (editing) await api.put(`/games/${editing.id}`, form);
      else await api.post("/games", form);
      toast.success(editing ? "Actualizado" : "Creado");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (g) => {
    if (!window.confirm(`Eliminar ${g.name}?`)) return;
    try { await api.delete(`/games/${g.id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="flex-1 bg-slate-50 min-h-full">
      <PageHeader
        title="Juegos de Lotería"
        subtitle="Configuración de sorteos, horarios y formato"
        testId="games-header"
        actions={
          <Button onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} className="bg-blue-600 hover:bg-blue-700 rounded-sm font-bold" data-testid="games-create-btn">
            <Plus className="w-4 h-4 mr-2" /> Nuevo juego
          </Button>
        }
      />
      <div className="p-6 sm:p-8">
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <Th>Código</Th><Th>Abrev.</Th><Th>Nombre</Th><Th>Operador</Th><Th>Sorteo</Th><Th>Cierre</Th><Th>Dígitos</Th><Th>Días</Th><Th>Estado</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {items.map((g) => (
                <tr key={g.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`game-row-${g.code}`}>
                  <td className="p-3 font-mono text-xs text-slate-900">{g.code}</td>
                  <td className="p-3 font-mono text-slate-700" data-testid={`game-abbr-${g.code}`}>{g.short_code || <span className="text-slate-400">{autoLotteryAbbr(g.name)}</span>}</td>
                  <td className="p-3 font-semibold text-slate-950">{g.name}</td>
                  <td className="p-3 text-slate-700">{g.operator_name}</td>
                  <td className="p-3 font-mono text-slate-900">{g.draw_time}</td>
                  <td className="p-3 font-mono text-red-700 font-bold">{g.closing_time}</td>
                  <td className="p-3 font-mono text-slate-700 text-center">{g.number_digits}</td>
                  <td className="p-3 font-mono text-xs text-slate-600">{g.active_days.map(d => DAYS[d]).join("")}</td>
                  <td className="p-3">
                    {g.active ? <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm bg-green-100 text-green-700">Activo</span> :
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm bg-slate-100 text-slate-600">Inactivo</span>}
                  </td>
                  <td className="p-3 text-right space-x-1">
                    <Button size="sm" variant="outline" onClick={() => { setEditing(g); setForm({ ...g, operator_id: g.operator_id, short_code: g.short_code || "" }); setOpen(true); }} className="h-8 rounded-sm border-slate-300" data-testid={`game-edit-${g.code}`}><Pencil className="w-3 h-3" /></Button>
                    <Button size="sm" variant="outline" onClick={() => remove(g)} className="h-8 rounded-sm border-red-300 text-red-700"><Trash2 className="w-3 h-3" /></Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan="10" className="p-8 text-center text-slate-500 text-sm">Sin juegos</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl rounded-sm">
          <DialogHeader><DialogTitle>{editing ? "Editar juego" : "Nuevo juego"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs font-semibold uppercase tracking-wider">Operador</Label>
              <Select value={form.operator_id} onValueChange={(v) => setForm({ ...form, operator_id: v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="game-form-operator"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent>{operators.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Código</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="mt-1 rounded-sm font-mono" data-testid="game-form-code" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Nombre</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm" data-testid="game-form-name" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs font-semibold uppercase tracking-wider">Abreviatura en ticket</Label>
              <Input maxLength={4} value={form.short_code} onChange={(e) => setForm({ ...form, short_code: e.target.value.replace(/\s/g, "") })} placeholder={`Automática: ${autoLotteryAbbr(form.name)}`} className="mt-1 rounded-sm font-mono" data-testid="game-form-short-code" />
              <p className="text-[11px] text-slate-500 mt-1">Se imprime en la columna "Loter." del ticket (ej. Ln -&gt; LEIDSA Noche). Déjalo vacío para usar la automática.</p>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Hora sorteo</Label>
              <Input value={form.draw_time} onChange={(e) => setForm({ ...form, draw_time: e.target.value })} className="mt-1 rounded-sm font-mono" placeholder="HH:MM" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Hora cierre</Label>
              <Input value={form.closing_time} onChange={(e) => setForm({ ...form, closing_time: e.target.value })} className="mt-1 rounded-sm font-mono" placeholder="HH:MM" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Dígitos</Label>
              <Input type="number" min="1" max="6" value={form.number_digits} onChange={(e) => setForm({ ...form, number_digits: parseInt(e.target.value) || 2 })} className="mt-1 rounded-sm font-mono" />
            </div>
            <div className="flex items-center gap-3 pt-6">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              <Label className="text-sm">Activo</Label>
            </div>
            <div className="col-span-2">
              <Label className="text-xs font-semibold uppercase tracking-wider">Días activos</Label>
              <div className="flex gap-1 mt-2">
                {DAYS.map((lbl, idx) => (
                  <button
                    key={lbl}
                    type="button"
                    onClick={() => toggleDay(idx)}
                    className={`w-10 h-10 rounded-sm text-sm font-bold font-mono transition-colors ${
                      form.active_days.includes(idx)
                        ? "bg-blue-600 text-white"
                        : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                    }`}
                    data-testid={`game-day-${idx}`}
                  >{lbl}</button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={save} className="bg-blue-600 hover:bg-blue-700 rounded-sm" data-testid="game-form-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Th = ({ children }) => <th className="p-3 text-xs uppercase tracking-wider font-bold text-slate-600">{children}</th>;
